import { Box, Button, Code, InlineStack, TextLabel, useSnackbars } from '@customerio/pluma-components/react';
import { CopyToClipboard } from '@/components/copy-to-clipboard';
import { useRegenerateSigningKey } from '@/services/db/webhook/mutations';

type WebhookSigningKeyFieldProps = {
  webhookId: string;
  signingKey: string;
  isDisabled: boolean;
  /** Called once the key has actually reached the clipboard. */
  onCopy: () => void;
};

/**
 * The signing key, with a copy affordance and a rotate button.
 *
 * Rotating is immediate and independent of the form's save: the new key is issued by its own
 * endpoint and lands in the cache straight away, so the user can copy it without saving first.
 */
export function WebhookSigningKeyField({ webhookId, signingKey, isDisabled, onCopy }: WebhookSigningKeyFieldProps) {
  const snackbars = useSnackbars();
  const regenerateSigningKey = useRegenerateSigningKey();

  const handleRegenerate = async () => {
    try {
      await regenerateSigningKey.mutateAsync({ webhookId });
    } catch {
      snackbars.create({
        variant: 'error',
        id: 'integrations.reporting-webhooks.signing-key.error',
        message: 'We ran into an issue creating a new signing key. Please try again.',
      });
    }
  };

  return (
    <Box>
      <TextLabel as="div" mb="050">
        Signing Key
      </TextLabel>
      <InlineStack alignBlock="center">
        <CopyToClipboard content={signingKey} idNamespace="reporting-webhook-signing-key" onCopy={onCopy}>
          <Code data-test-webhook-signing-key>{signingKey}</Code>
        </CopyToClipboard>
        <Button
          data-test-webhook-regenerate-signing-key
          variant="secondary"
          ml="150"
          isDisabled={isDisabled || regenerateSigningKey.isPending}
          isLoading={regenerateSigningKey.isPending}
          onClick={() => {
            void handleRegenerate();
          }}
        >
          Regenerate
        </Button>
      </InlineStack>
    </Box>
  );
}
